/** Default browser tab title (keep in sync with public/index.html) */
export const DOCUMENT_TITLE_BASE = 'flowdo';

export function formatPageTitle(pageLabel) {
  if (!pageLabel) return DOCUMENT_TITLE_BASE;
  return `${DOCUMENT_TITLE_BASE} - ${pageLabel}`;
}

function normalizePath(pathname) {
  if (!pathname) return '/';
  let p = pathname.split('?')[0];
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

/** Static path → short page name (first match in getPageTitleFromPath uses EXACT, then regex rules). */
const EXACT_TITLES = {
  '/': 'Business management system',
  '/home': 'Dashboard',
  '/about-us': 'About Us',
  '/products': 'Products',
  '/products/task': 'Tasks product',
  '/products/documents': 'Documents product',
  '/products/credentials-vault': 'Credentials Vault product',
  '/pricing': 'Pricing',
  '/contact-us': 'Contact Us',
  '/login': 'Login',
  '/register': 'Register',
  '/terms-and-conditions': 'Terms & Conditions',
  '/refund-cancellation-policy': 'Refund & Cancellation',
  '/privacy-policy': 'Privacy Policy',
  '/forgot-password': 'Forgot password',
  '/reset-password': 'Reset password',
  '/registration-subscription': 'Registration & subscription',
  '/test-subscription': 'Test subscription',
  '/shared': 'Shared document',
  '/page-lock-screen': 'Lock screen',
  '/page-error-400': 'Error 400',
  '/page-error-403': 'Error 403',
  '/page-error-404': 'Error 404',
  '/page-error-500': 'Error 500',
  '/page-error-503': 'Error 503',
  '/dashboard': 'Dashboard',
  '/dashboard-dark': 'Dashboard',
  '/task/tasklist': 'Tasks',
  '/task/addtask': 'Add task',
  '/task/edittask': 'Edit task',
  '/task/taskboard': 'Task board',
  '/taskboard': 'Task board',
  '/task/worksheet': 'Worksheet',
  '/task/performance': 'Performance Dashboard',
  '/task/performance/details': 'Performance details',
  '/task/projectlist': 'Projects',
  '/task/addproject': 'Add project',
  '/task/editproject': 'Edit project',
  '/subscription-feature-required': 'Subscription required',
  '/modules': 'Modules',
  '/subscribe': 'Subscription',
  '/user-subscription': 'My subscription',
  '/edit-profile': 'Edit profile',
  '/changePassword': 'Change password',
  '/settings/userlist': 'Users',
  '/settings/adduser': 'Add user',
  '/settings/edituser': 'Edit user',
  '/settings/userSettings': 'User settings',
  '/settings/accessControl': 'Access control',
  '/settings/branchList': 'Branches',
  '/settings/addBranch': 'Add branch',
  '/settings/departmentList': 'Departments',
  '/settings/addDepartment': 'Add department',
  '/settings/subDepartmentList': 'Sub-departments',
  '/settings/addSubDepartment': 'Add sub-department',
  '/settings/designationList': 'Designations',
  '/settings/addDesignation': 'Add designation',
  '/staff/staffList': 'Staff',
  '/staff/addStaff': 'Add staff',
  '/credential-vault/credentialVaultList': 'Corporate credentials',
  '/credential-vault/personalVaultList': 'Personal credentials',
  '/credential-vault/vaultSettings': 'Vault settings',
  '/credential-vault/addCredential': 'Add a new Credential',
  '/vehicle/addVehicle': 'Add vehicle',
  '/vehicle/vehicleList': 'Vehicles',
  '/documents/mydocuments': 'My documents',
  '/documents/dashboard': 'Documents dashboard',
  '/documents/shared': 'Shared documents',
  '/documents/favorites': 'Favorite documents',
  '/documents/upload': 'Upload document',
  '/documents/addDocumentClassification': 'Add classification',
  '/documents/documentClassifications': 'Classifications',
  '/subscription/checkout': 'Checkout',
  '/subscription/renew': 'Renew subscription',
  '/subscription/payment-result': 'Payment',
  '/subscription/payment-success': 'Payment successful',
};

/** [regex, title] — order matters; more specific patterns first. */
const REGEX_TITLE_RULES = [
  [/^\/task\/projectdetails\/[^/]+\/daily-updates$/, 'Project daily updates'],
  [/^\/task\/projectdetails\/[^/]+$/, 'Project details'],
  [/^\/task\/taskdetails\/[^/]+\/daily-updates$/, 'Task daily updates'],
  [/^\/task\/taskdetails\/[^/]+$/, 'Task details'],
  [/^\/documents\/folder\/[^/]+$/, 'Documents folder'],
  [/^\/documents\/details\/[^/]+$/, 'Document details'],
  [/^\/documents\/edit\/[^/]+$/, 'Edit document'],
  [/^\/documents\/editDocumentClassification\/[^/]+$/, 'Edit classification'],
  [/^\/settings\/editBranch\/[^/]+$/, 'Edit branch'],
  [/^\/settings\/editDepartment\/[^/]+$/, 'Edit department'],
  [/^\/settings\/editSubDepartment\/[^/]+$/, 'Edit sub-department'],
  [/^\/staff\/editStaff\/[^/]+$/, 'Edit staff'],
  [/^\/staff\/staffDetails\/[^/]+$/, 'Staff details'],
  [/^\/staff\/[^/]+\/address\/(add|[^/]+\/edit)$/, 'Staff address'],
  [/^\/staff\/[^/]+\/contact\/(add|[^/]+\/edit)$/, 'Staff contact'],
  [/^\/staff\/[^/]+\/employment\/(add|[^/]+\/edit)$/, 'Staff employment'],
  [/^\/staff\/[^/]+\/document\/add$/, 'Add staff document'],
  [/^\/staff\/[^/]+\/document\/[^/]+\/edit$/, 'Edit staff document'],
  [/^\/staff\/[^/]+\/document\/[^/]+$/, 'Staff document'],
  [/^\/credential-vault\/credentialDetail\/[^/]+$/, 'Credential Details'],
  [/^\/credential-vault\/editCredential\/[^/]+$/, 'Edit Credential'],
  [/^\/vehicle\/editVehicle\/[^/]+$/, 'Edit vehicle'],
  [/^\/vehicle\/vehicleDetails\/[^/]+$/, 'Vehicle details'],
  [/^\/vehicle\/[^/]+\/document\/add$/, 'Add vehicle document'],
  [/^\/vehicle\/[^/]+\/document\/[^/]+\/edit$/, 'Edit vehicle document'],
  [/^\/vehicle\/[^/]+\/document\/[^/]+$/, 'Vehicle document'],
  [/^\/vehicle\/[^/]+\/insurance\/(add|[^/]+\/edit)$/, 'Vehicle insurance'],
  [/^\/vehicle\/[^/]+\/registration\/(add|[^/]+\/edit)$/, 'Vehicle registration'],
];

export function getPageTitleFromPath(pathname) {
  const path = normalizePath(pathname);
  const exact = EXACT_TITLES[path];
  if (exact) return formatPageTitle(exact);

  for (const [re, title] of REGEX_TITLE_RULES) {
    if (re.test(path)) return formatPageTitle(title);
  }

  return DOCUMENT_TITLE_BASE;
}
